/**
 * Tracks the user's browsing history across the app (recordings + pictures)
 * and their recent search queries. Uses localStorage — public app, no auth required.
 */

const REC_KEY = 'vaad_history_recordings_v1';
const PIC_KEY = 'vaad_history_pictures_v1';
const SEARCH_KEY = 'vaad_search_history_v1';
const MAX_RECORDINGS = 60;
const MAX_PICTURES = 60;
const MAX_SEARCHES = 8;

const readList = (key) => {
  try {
    const raw = localStorage.getItem(key);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
};

const writeList = (key, arr) => {
  try {
    localStorage.setItem(key, JSON.stringify(arr));
  } catch { /* quota */ }
};

const dedupePush = (arr, id, entry, max) => {
  const filtered = arr.filter((e) => e && e.id !== id);
  filtered.unshift(entry);
  return filtered.slice(0, max);
};

// ---------- Recordings ----------
export const trackRecordingVisit = (recording) => {
  if (!recording) return;
  const id = recording.drive_file_id || recording.id;
  if (!id) return;
  const entry = {
    id,
    drive_file_id: recording.drive_file_id || id,
    title: recording.title || 'Untitled',
    speaker: recording.speaker || '',
    cover_image: recording.cover_image || null,
    duration: recording.duration || null,
    parsha: recording.parsha || null,
    visited_at: Date.now(),
  };
  writeList(REC_KEY, dedupePush(readList(REC_KEY), id, entry, MAX_RECORDINGS));
};

export const listRecordingHistory = (limit = MAX_RECORDINGS) =>
  readList(REC_KEY).slice(0, limit);

// ---------- Pictures ----------
export const trackPictureVisit = (picture) => {
  if (!picture) return;
  const id = picture.drive_file_id || picture.id;
  if (!id) return;
  const entry = {
    id,
    drive_file_id: picture.drive_file_id || id,
    title: picture.title || picture.name || 'Photo',
    thumbnail_url: picture.thumbnail_url || picture.image_url || null,
    speaker: picture.speaker || picture.person || null,
    visited_at: Date.now(),
  };
  writeList(PIC_KEY, dedupePush(readList(PIC_KEY), id, entry, MAX_PICTURES));
};

export const listPictureHistory = (limit = MAX_PICTURES) =>
  readList(PIC_KEY).slice(0, limit);

// ---------- Search queries ----------
export const trackSearchQuery = (q) => {
  const query = (q || '').trim();
  if (!query || query.length < 2) return;
  const arr = readList(SEARCH_KEY).filter(
    (item) => item && item.q && item.q.toLowerCase() !== query.toLowerCase()
  );
  arr.unshift({ q: query, at: Date.now() });
  writeList(SEARCH_KEY, arr.slice(0, MAX_SEARCHES));
};

export const listSearchHistory = () => readList(SEARCH_KEY);

export const removeSearchQuery = (q) => {
  const arr = readList(SEARCH_KEY).filter(
    (item) => item && item.q && item.q.toLowerCase() !== (q || '').toLowerCase()
  );
  writeList(SEARCH_KEY, arr);
};

export const clearSearchHistory = () => writeList(SEARCH_KEY, []);

// ---------- Utility clears ----------
export const clearRecordingHistory = () => writeList(REC_KEY, []);
export const clearPictureHistory = () => writeList(PIC_KEY, []);

export const removeRecordingHistory = (id) => {
  writeList(REC_KEY, readList(REC_KEY).filter((e) => e && e.id !== id));
};

export const removePictureHistory = (id) => {
  writeList(PIC_KEY, readList(PIC_KEY).filter((e) => e && e.id !== id));
};
